import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Globe, Calendar, MapPin, Zap, Clock, DollarSign, Users, HeadsetIcon, ArrowRight, Mail, Phone, CheckCircle, Sparkles, Thermometer } from 'lucide-react';

const ACKDemosPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const services = [
    {
      icon: Globe,
      title: 'Website Development',
      description: 'Custom, mobile-responsive websites that convert visitors into customers.'
    },
    {
      icon: Calendar,
      title: 'Online Booking Integration',
      description: 'Streamlined appointment systems that work 24/7 for your business.'
    },
    {
      icon: MapPin,
      title: 'Local SEO & Google Maps',
      description: 'Get found by customers searching for your services in your area.'
    },
    {
      icon: Zap,
      title: 'Digital Process Automation',
      description: 'Automate repetitive tasks and focus on growing your business.'
    }
  ];

  const whyChooseUs = [
    { icon: Clock, title: 'Fast Turnaround', description: 'Launch your project in weeks, not months' },
    { icon: DollarSign, title: 'Transparent Pricing', description: 'No hidden fees or surprise costs' },
    { icon: Users, title: 'Local Market Understanding', description: 'We know what works for local businesses' },
    { icon: HeadsetIcon, title: 'Ongoing Support', description: 'We\'re here when you need us' }
  ];

  const demos = [
    {
      title: 'Beauty Salon Demo',
      description: 'Professional booking website for beauty salons, threading studios, and spas.',
      icon: Sparkles,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      link: '/demos/beauty-salon'
    },
    {
      title: 'HVAC Contractor Demo',
      description: 'Service request website for HVAC companies with emergency callout features.',
      icon: Thermometer,
      color: 'from-blue-600 to-blue-800',
      bgColor: 'bg-blue-50',
      link: '/demos/hvac-contractor'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/6184d9d2-12c2-428c-93bd-006d03294f4e/images/6f2f596bbd99566ef266b388a183b62fb4b87d478a6ea3041cef046e34552b4c.png"
              alt="ACK Demos Logo"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">ACK Demos</h1>
              <p className="text-xs text-slate-500">Digital Solutions</p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="#demos" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Demos</a>
            <a href="#services" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Services</a>
            <a href="#why-us" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Why Us</a>
            <a href="#contact" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Contact</a>
          </div>

          <div className="flex items-center gap-4">
            <a 
              href="tel:2812585542"
              className="hidden md:flex items-center gap-2 text-slate-700 font-medium hover:text-blue-600"
            >
              <Phone size={18} />
              (281) 258-5542
            </a>
            <Button
              data-testid="nav-consultation-btn"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Call Button */}
      <a
        href="tel:2812585542"
        className="fixed bottom-4 right-4 z-50 md:hidden bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold"
      >
        <Phone size={18} />
        Call Now
      </a>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-6">
              Digital Solutions for Growing Local Businesses
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Helping Local Businesses Grow Through{' '}
              <span className="text-blue-600">Digital Transformation</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Websites, Online Booking Systems, and Local Search Optimization Designed to Increase Revenue
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-consultation-btn"
                size="lg"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6"
              >
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-demo-btn"
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('demos').scrollIntoView({ behavior: 'smooth' })}
                className="border-slate-300 text-slate-700 hover:bg-slate-100 text-lg px-8 py-6"
              >
                View Demo Projects
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Demo Showcase Section */}
      <section id="demos" className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Industry Demo Showcase
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Industry-specific website demos designed to convert visitors into customers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {demos.map((demo, idx) => (
              <motion.div
                key={demo.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className={`${demo.bgColor} p-8 rounded-2xl border border-slate-200 hover:shadow-xl transition-all duration-300 group`}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${demo.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <demo.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{demo.title}</h3>
                <p className="text-slate-600 mb-6">{demo.description}</p>
                <Button
                  data-testid={`demo-${idx}-btn`}
                  onClick={() => navigate(demo.link)}
                  className={`bg-gradient-to-r ${demo.color} hover:opacity-90 text-white`}
                >
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Modernize Your Business Operations
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              ACK Demos helps local businesses thrive in the digital age. We build professional websites, 
              implement smart automation systems, and optimize your online presence to bring more customers 
              through your door. Our solutions are designed specifically for small to medium businesses 
              looking to increase revenue without the complexity or high costs of enterprise solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to establish a strong digital presence and grow your local business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-slate-50 p-8 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                  <service.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why ACK Demos
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We understand local businesses because we work with them every day
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="text-center p-6 bg-white rounded-xl"
              >
                <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Modernize Your Business?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Let's discuss how we can help you attract more customers and streamline your operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-slate-800 rounded-xl p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                      <Input
                        data-testid="contact-name-input"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Tell us about your project</label>
                      <Textarea
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="What does your business do? What challenges are you facing?"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 min-h-[120px]"
                        required
                      />
                    </div>
                    <Button
                      data-testid="contact-submit-btn"
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
                    >
                      Book a Free Consultation
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
                    <p className="text-slate-300">We'll be in touch within 24 hours to schedule your consultation.</p>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <h3 className="text-2xl font-semibold mb-6">Get in Touch</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Phone</p>
                    <a href="tel:2812585542" className="text-slate-300 hover:text-white">(281) 258-5542</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <p className="text-slate-400">contact@ackdemos.com</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-slate-800 rounded-xl">
                <h4 className="font-semibold mb-3">What to Expect</h4>
                <ul className="space-y-3 text-slate-300 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>15-minute discovery call to understand your needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Custom proposal with transparent pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>No pressure, no obligation</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/6184d9d2-12c2-428c-93bd-006d03294f4e/images/6f2f596bbd99566ef266b388a183b62fb4b87d478a6ea3041cef046e34552b4c.png"
                alt="ACK Demos Logo"
                className="h-10 w-auto"
              />
              <div>
                <h4 className="text-lg font-bold text-white">ACK Demos</h4>
                <p className="text-xs text-slate-400">Digital Solutions for Growing Local Businesses</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-slate-400">
              <a href="tel:2812585542" className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone size={16} />
                (281) 258-5542
              </a>
            </div>
            
            <div className="flex gap-8 text-sm text-slate-400">
              <a href="#demos" className="hover:text-white transition-colors">Demos</a>
              <a href="#services" className="hover:text-white transition-colors">Services</a>
              <a href="#why-us" className="hover:text-white transition-colors">Why Us</a>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-500">
              © 2026 ACK Demos. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ACKDemosPage;
